// In-house language-service provider for MSSQL documents (Plan 20 §10
// step 6 — "retire the mssql document-LSP registration"). Replaces the
// deleted `sqlToolsLanguageServiceProvider.ts`: mssql documents are no
// longer registered with SqlToolsService at all — no `textDocument/didOpen`
// / `didChange` / `didClose`, no language-service `connection/connect`, no
// `workspace/didChangeConfiguration`, and no `textDocument/intelliSenseReady`
// wait. Every per-document feature is served renderer-side:
//
// - **Completion** — orchestrator/resolver-driven (`mssqlResolver` reads the
//   schema-bus snapshot); the provider's `getCompletionItems` is consulted
//   only by the mock engine's LSP bridge, so it returns empty here.
// - **Hover** — the routine-only arm (§10 step 3): composes
//   `getMssqlRoutineHoverInfo` and deliberately returns `null` for table
//   references, because SqlMonaco's always-on bus-based table-card hover
//   (which fires for every engine) already renders those — returning table
//   content here would stack two identical cards in one hover popup.
// - **Signature help** — `null` (§10 step 4): real parameter hints come from
//   the independently-registered `computeFunctionSignatureHelp` provider.
// - **Diagnostics** — `onDiagnostics` is deliberately ABSENT: the in-house
//   `sql-syntax` marker owner (§10 step 2) is the only mssql squiggle source.
// - **Status pill** — derived from the schema-bus snapshot lifecycle
//   (mirroring `mysqlLanguageServiceProvider`): `connectForLanguageServices`
//   emits `initializing`, warms `ensureMssqlTables` (which publishes the bus
//   snapshot), then emits `ready`/`error`. A bus subscription additionally
//   flips the pill on ANY snapshot publish for the document's
//   `(connectionId, database)` — so a warm driven elsewhere (e.g. the
//   resolver's cold-cache fire-and-forget) recovers a failed connect, and
//   post-DDL republish re-emits `ready` (SqlMonaco uses that to re-trigger
//   an open suggest widget). Every `ready` flip is filtered through
//   `resolveSchemaReadyStatus` (S7 item 5): while an introspection row cap
//   has clipped an artefact for this (connectionId, database), the pill
//   emits `partial` with the artefact-naming message instead.

import type {
  LanguageServiceProvider,
  LanguageServiceCompletionResult,
  IntelliSenseStatus,
  IntelliSenseStatusListener,
  LanguageServicePosition,
  LanguageServiceConnectionInfo,
} from './languageServiceProvider';
import {
  getSnapshot as getSchemaBusSnapshot,
  subscribe as subscribeSchemaBus,
} from '@/features/intellisense/schemaBus/schemaBus';
import { ensureMssqlTables, getCachedMssqlTables, DEFAULT_TTL_MS } from './mssqlTableCache';
import { getMssqlRoutineHoverInfo } from './mssqlHover';
import { resolveSchemaReadyStatus, subscribeSchemaReadyStatus } from './introspectionTruncation';

type DocumentState = {
  text: string;
  connectionId?: string;
  database?: string;
  status: IntelliSenseStatus;
  statusMessage?: string;
  /** Bus subscription cleanup for the connected document. */
  unsubscribeBus?: () => void;
};

const documents = new Map<string, DocumentState>();
const statusListeners: IntelliSenseStatusListener[] = [];

// The cache's own TTL, reused rather than hand-duplicated (T6 review M5).
// Since S7 item 3 it is effectively "never expire on a timer" (6 hours) and
// the connect-time warm no longer forces a refresh on age — see
// `connectForLanguageServices`.
const SCHEMA_TTL_MS = DEFAULT_TTL_MS;

// mssql database names are case-insensitive server-side (and the bus'
// `SchemaSnapshot.database` / a document's registered database can come from
// differently-cased sources — e.g. a connection default vs. a `USE` warm) —
// compare case-insensitively so a same-database publish never gets treated
// as "a different database" (T6 review M4).
function sameDatabase(a: string | undefined, b: string | undefined): boolean {
  return (a ?? '').toLowerCase() === (b ?? '').toLowerCase();
}

function emitStatus(uri: string, status: IntelliSenseStatus, message?: string): void {
  const doc = documents.get(uri);
  // S7 item 5 — a `ready` flip becomes `partial` (same "completion is live"
  // meaning, minus the artefacts a row cap clipped) whenever a clip stands
  // for this document's (connectionId, database). Applied here rather than at
  // each call site so BOTH readiness paths — the connect-time warm and the
  // bus `updated` subscription below — stay honest, including post-DDL
  // republishes.
  let nextStatus = status;
  let nextMessage = message;
  if (status === 'ready') {
    const resolved = resolveSchemaReadyStatus(doc?.connectionId, doc?.database);
    nextStatus = resolved.status;
    nextMessage = resolved.message ?? message;
  }

  if (doc) {
    doc.status = nextStatus;
    doc.statusMessage = nextMessage;
  }

  for (const listener of statusListeners) {
    listener({ uri, status: nextStatus, message: nextMessage });
  }
}

function getDocument(uri: string): DocumentState {
  return documents.get(uri) ?? { text: '', status: 'unavailable' };
}

// A clip or failure recorded AFTER a document went `ready` (phase 2 lands
// later than the readiness emit) must still reach the pill, and a clean
// retry must take it back — re-derive every affected document's status
// whenever the registry changes (release review 1.5.0, R2 / R3).
subscribeSchemaReadyStatus((connectionId, database) => {
  for (const [uri, doc] of documents) {
    if (doc.connectionId !== connectionId) continue;
    if (!sameDatabase(doc.database, database)) continue;
    if (doc.status !== 'ready' && doc.status !== 'partial') continue;
    emitStatus(uri, 'ready');
  }
});

function teardownDocument(uri: string): void {
  const doc = documents.get(uri);
  doc?.unsubscribeBus?.();
  if (doc) doc.unsubscribeBus = undefined;
}

export const mssqlLanguageServiceProvider: LanguageServiceProvider = {
  engine: 'mssql',
  ensureInitialized: async () => {},

  onIntelliSenseStatus: (listener) => {
    statusListeners.push(listener);
    return () => {
      const idx = statusListeners.indexOf(listener);
      if (idx >= 0) statusListeners.splice(idx, 1);
    };
  },

  getIntelliSenseStatus: (uri) => documents.get(uri)?.status ?? 'unavailable',

  registerDocument: async (uri: string, text: string) => {
    teardownDocument(uri);
    documents.set(uri, { text, status: 'unavailable' });
    return true;
  },

  updateDocument: async (uri: string, text: string) => {
    const existing = documents.get(uri);
    if (existing) {
      existing.text = text;
    } else {
      documents.set(uri, { text, status: 'unavailable' });
    }
    return true;
  },

  closeDocument: async (uri: string) => {
    teardownDocument(uri);
    documents.delete(uri);
    return true;
  },

  connectForLanguageServices: async (
    uri: string,
    connectionInfo: LanguageServiceConnectionInfo,
  ): Promise<boolean> => {
    const doc = documents.get(uri) ?? { text: '', status: 'unavailable' };
    doc.connectionId = connectionInfo.connectionId;
    doc.database = connectionInfo.database?.trim() || '';
    documents.set(uri, doc);

    // Bus-driven readiness: any snapshot publish for this document's
    // (connectionId, database) means schema-aware IntelliSense is live —
    // regardless of which caller warmed the cache. `disconnected` (fired by
    // `invalidateConnection` on connection close) parks the pill.
    doc.unsubscribeBus?.();
    doc.unsubscribeBus = subscribeSchemaBus(connectionInfo.connectionId, (event) => {
      if (event.type === 'updated' && sameDatabase(event.database, doc.database)) {
        emitStatus(uri, 'ready');
      } else if (event.type === 'disconnected') {
        emitStatus(uri, 'unavailable');
      }
    });

    emitStatus(uri, 'initializing', 'Loading SQL Server schema…');

    try {
      // S7 item 3 — connecting a SECOND document to an already-warm connection
      // must not re-run the introspection fan-out. A warm snapshot (any phase:
      // phase 1 alone already answers table completion, and its phase 2 is
      // still filling in) is reused as-is; only a missing one forces the
      // refresh. Age is deliberately NOT a reason to refetch any more — the
      // snapshot is invalidated by DDL / explicit refresh / reconnect.
      const existingSnapshot = getCachedMssqlTables(doc.connectionId, doc.database);
      const shouldForceRefresh = !existingSnapshot;

      await ensureMssqlTables({
        connectionId: doc.connectionId,
        database: doc.database,
        forceRefresh: shouldForceRefresh,
        ttlMs: SCHEMA_TTL_MS,
      });
      emitStatus(uri, 'ready');
      return true;
    } catch (error) {
      // `false` makes ConnectionSyncManager schedule its bounded retries; the
      // bus subscription above additionally recovers the pill if any other
      // warm path succeeds first.
      emitStatus(
        uri,
        'error',
        error instanceof Error ? error.message : 'Failed to load SQL Server schema',
      );
      return false;
    }
  },

  disconnectForLanguageServices: async (uri: string) => {
    teardownDocument(uri);
    const doc = documents.get(uri);
    if (doc) {
      doc.connectionId = undefined;
      doc.database = undefined;
      emitStatus(uri, 'unavailable');
    }
    return true;
  },

  // mssql completion is fully orchestrator-driven (`mssqlResolver` +
  // schema-bus snapshot). Only the mock engine's LSP bridge ever consults a
  // provider for completion items, so this arm is intentionally empty — it
  // must never become a second completion source.
  getCompletionItems: async (): Promise<LanguageServiceCompletionResult> => ({ items: [] }),

  // §10 step 3 — routine-only hover arm. Table/view hover is NOT served here:
  // SqlMonaco's always-on bus-based table-card provider already renders the
  // full column list for table references (for every engine, independent of
  // this provider), so this arm composes ONLY the routine lookup — see
  // `mssqlHover.ts`'s `getMssqlRoutineHoverInfo` for the split rationale.
  getHoverInfo: async (uri: string, position: LanguageServicePosition) => {
    const doc = getDocument(uri);
    if (!doc.connectionId) return null;
    const snapshot = getSchemaBusSnapshot(doc.connectionId, doc.database);
    if (!snapshot) return null;
    return getMssqlRoutineHoverInfo({ text: doc.text, position, snapshot });
  },

  // §10 step 4 — the generic LSP-gated signature-help registration in
  // SqlMonaco falls through instantly; real parameter hints come from the
  // independently-registered in-house provider (`computeFunctionSignatureHelp`
  // backed by `get-routine-metadata` / the built-in catalog).
  getSignatureHelp: async () => null,
};
